
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { EXCLUDED_FILTERS_KEYS, excludeMatchingFields, getBudgetLabelString, getFilterValues, getHotelsFiltersNotEmptyFields, getHotelsFiltersType, getInitialHotelFiltersState, handleFilterRemove, isCarsFiltersEmpty, isFiltersEmpty } from '@utils';
import { getSelectedCurrency, RootState } from '@share/utils';
import { carsFiltersActions, filtersActions } from '@share/store/slices';
import { HomesTypes } from '@share/constants';
import { IAccount } from '@share/common-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

import './style.scss';

interface IProps {
  isVacationRentals?: boolean | null | undefined;
  isCars?: boolean | null | undefined;

  displayClearAll?: boolean;

  onFiltersOrSortChange: () => void;
}

export function SelectedFiltersItem(props: IProps) {
  const { isVacationRentals, isCars, displayClearAll } = props;

  const filtersStore = useSelector((state: RootState) => state.filtersStore);
  const hotelsStore = useSelector((state: RootState) => state.hotelsStore);
  const carsFiltersStore = useSelector((state: RootState) => state.carsFiltersStore);
  const carsStore = useSelector((state: RootState) => state.carsStore);
  const account = useSelector((state: RootState) => state.loginStore.account);

  const { loadingFilter: loadingFilterHotels } = hotelsStore;
  const { loadingFilter: loadingFilterCars } = carsStore;

  const { searchHomes } = filtersStore;
  
  const hotelInitialFilterState = getInitialHotelFiltersState(account as IAccount, isVacationRentals);
  const isFiltersAppliedHotels = !isFiltersEmpty(filtersStore, account as IAccount, isVacationRentals);
  const isFiltersAppliedCars = !isCarsFiltersEmpty(carsFiltersStore);
  
  const notEmptyFieldsHotels = !isCars ? getHotelsFiltersNotEmptyFields(filtersStore, account as IAccount, isVacationRentals) : [];

  const loadingFilter = isCars ? loadingFilterCars : loadingFilterHotels;
  const isFiltersApplied = isCars ? isFiltersAppliedCars : isFiltersAppliedHotels;
  const store = isCars ? carsFiltersStore : filtersStore;

  const hotelFilterNotExcluded = excludeMatchingFields(filtersStore, hotelInitialFilterState, EXCLUDED_FILTERS_KEYS);

  const isSearchHomesApplied = isFiltersAppliedHotels && !isCars && !isVacationRentals && searchHomes !== HomesTypes.All && !!hotelFilterNotExcluded?.searchHomes;

  const filterValuesObject = getFilterValues(store, notEmptyFieldsHotels, isCars);
  const filterValues = isCars ? 
                          filterValuesObject : 
                          filterValuesObject?.filter((f) => Object.keys(hotelFilterNotExcluded).includes(f.key));
  
  if (!isFiltersApplied || loadingFilter || (!filterValues.length && !isSearchHomesApplied && !isCars)) {
    return null;
  }

  const dispatch = useDispatch();

  const currency = getSelectedCurrency(account as IAccount);
  
  const handleRemove = (key: string, value: string, isArray: boolean) => {
    handleFilterRemove(isCars, filtersStore, dispatch, key, value, isArray);
    props.onFiltersOrSortChange();
  };

  const handleRemoveClearAll = () => {
    if(isCars) {
      dispatch(carsFiltersActions.resetFilters());
    } else {
      const restrictedVacationRentalsList = account?.restrictedVacationRentalsList;
      const type = getHotelsFiltersType(account as IAccount, isVacationRentals);
      const filterType = { type , list: restrictedVacationRentalsList };
      dispatch(filtersActions.resetFiltersByType({ filterType, isQuickEnabled: account?.hasQuick || false }));

    }
    props.onFiltersOrSortChange();
  };

  const handleRemoveSearchHomes = () => {
    dispatch(filtersActions.setSearchHomes(HomesTypes.All));
    props.onFiltersOrSortChange();
  };

  return (
    <div className="selected-filters-header__content">
      {filterValues.map((f) => (
        <div key={`${f.key}-${f.value}`} className="selected-filters-header__filter" onClick={() => handleRemove(f.key, f.value, f.isArray)}>
          <span className="selected-filters-header__filter-value">
            {f.key === 'budget' ? 
                getBudgetLabelString(f.value, currency) : 
                f.key === 'starRatings' ? 
                    <FormattedMessage id="stars.count" values={{ count: f.value }} /> : 
                    f.value}
          </span>
          <FontAwesomeIcon icon={faTimes} />
        </div>))}

      {isSearchHomesApplied && (
        <div className="selected-filters-header__filter" onClick={handleRemoveSearchHomes}>
          <span className="selected-filters-header__filter-value">
            <FormattedMessage id={searchHomes === HomesTypes.VacationRentalOnly ? "houses" : "Hotels"} />
          </span>
          <FontAwesomeIcon icon={faTimes} />
        </div>)}

      {displayClearAll && filterValues.length > 1 && (
        <div className="selected-filters-header__filter clear-all-button" onClick={handleRemoveClearAll}>
          <span className="selected-filters-header__filter-value">
            <FormattedMessage id="clearAll" />
          </span>
        </div>)}
    </div>
  );
}
